import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Paper, Typography, Box,
  RadioGroup, FormControlLabel, Radio, FormControl, FormGroup, Checkbox, TextField,
  Select, MenuItem, Alert,
} from '@mui/material';
import HelpIcon from './HelpIcon';

/**
 * ALL — Average Longitude Lines.
 *
 * <h2>Two controls, not three</h2>
 * The study offers a centre, a zodiac, and a "combine both centres" mode. Modelling those as three
 * independent groups makes contradictory states reachable — Geo selected in one group while
 * Geo-Helio-Sidereal is selected in another — and then something has to decide which wins, silently.
 *
 * <p>So it is a SYSTEM (Geo, Helio, or Geo+Helio) crossed with a ZODIAC (Sidereal or Tropical).
 * That yields exactly the six configurations asked for, including both dual ones, and no state
 * where two controls disagree. The dual rows are still named Geo-Helio-Sidereal and
 * Geo-Helio-Tropical where the user reads them, in the summary line under the controls.
 *
 * <h2>Bodies with no heliocentric position</h2>
 * The Sun and the lunar nodes do not exist heliocentrically — the Sun is that frame's origin, and
 * the nodes are defined by the Moon's orbit around Earth. In a pure Helio study they are disabled
 * here, because the backend refuses them and a control that produces a guaranteed error is worse
 * than one that is visibly unavailable. In a Geo+Helio study they stay selectable and contribute
 * their geocentric longitude alone, which the backend reports back and the caller can state.
 */

const BODIES = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter',
  'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu',
];

/** Bodies the heliocentric frame cannot express. Mirrors HeliocentricBodies on the backend. */
const NO_HELIOCENTRIC_POSITION = ['Sun', 'Rahu', 'Ketu'];

const SYSTEMS = [
  { value: 'geo', label: 'Geocentric' },
  { value: 'helio', label: 'Heliocentric' },
  { value: 'dual', label: 'Geo + Helio (combined)' },
];

const ZODIACS = [
  { value: 'sidereal', label: 'Sidereal' },
  { value: 'tropical', label: 'Tropical' },
];

const COLORS = [
  'Red', 'Green', 'Blue', 'Orange', 'Purple', 'Brown', 'DeepPink', 'Teal', 'Black', 'Gray',
];

const LINE_TYPES = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
];

/** The wire value the backend's ZodiacFrame.from() resolves, for a single-system study. */
export function frameFor(system, zodiac) {
  if (system === 'helio') return zodiac === 'sidereal' ? 'helio-sidereal' : 'helio';
  return zodiac === 'sidereal' ? 'geo-sidereal' : 'geo-tropical';
}

/** What the chosen configuration is called, in the words the study is specified in. */
export function describeConfiguration(system, zodiac) {
  const z = zodiac === 'sidereal' ? 'Sidereal' : 'Tropical';
  if (system === 'dual') return `Geo-Helio-${z}`;
  return `${system === 'helio' ? 'Helio' : 'Geo'}-${z}`;
}

/** Bodies that cannot be used in the chosen system. Empty unless it is a pure heliocentric one. */
export function unavailableBodies(system) {
  return system === 'helio' ? NO_HELIOCENTRIC_POSITION : [];
}

const AverageLongitudeDialog = ({ open, onClose, onRun }) => {
  const [system, setSystem] = useState('geo');
  const [zodiac, setZodiac] = useState('sidereal');
  const [selected, setSelected] = useState(['Sun', 'Moon', 'Mercury', 'Venus', 'Mars']);

  // The same rendering controls the Planetary Lines study uses, because ALL draws through that
  // pipeline: one line, mapped to price by pricePer360 and stacked into octaves.
  const [pricePer360, setPricePer360] = useState('200');
  const [color, setColor] = useState('Purple');
  const [thickness, setThickness] = useState('medium');
  const [lineType, setLineType] = useState('solid');
  const [autoFitOctaves, setAutoFitOctaves] = useState(true);
  const [octaveStart, setOctaveStart] = useState('0');
  const [octaveEnd, setOctaveEnd] = useState('3');

  const unavailable = unavailableBodies(system);
  const usable = selected.filter(b => !unavailable.includes(b));
  const droppedBySystem = selected.filter(b => unavailable.includes(b));
  // Reported rather than silently included: in a combined study these bodies contribute one
  // longitude where every other body contributes two, which shifts the average.
  const partial = system === 'dual'
    ? selected.filter(b => NO_HELIOCENTRIC_POSITION.includes(b))
    : [];

  const toggleBody = (body) => {
    setSelected(prev => (prev.includes(body) ? prev.filter(b => b !== body) : [...prev, body]));
  };

  const handleRun = () => {
    onRun({
      planets: usable,
      system,
      zodiac,
      // Both are sent: `frame` is what a single-system study is read in, and the backend ignores
      // its centre when dualSystem is set — it needs the zodiac from it either way.
      frame: frameFor(system, zodiac),
      dualSystem: system === 'dual',
      label: `ALL ${describeConfiguration(system, zodiac)}`,
      color,
      lineType,
      thickness,
      pricePer360,
      autoFitOctaves,
      octaveStart,
      octaveEnd,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md"
            PaperProps={{ style: { minWidth: 620, borderRadius: 8 } }}>
      <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>ALL — Average Longitude Lines</span>
        <HelpIcon helpKey="averageLongitudeDialog" />
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Box display="flex" gap={4} flexWrap="wrap">
            <FormControl>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 0.5 }}>System</Typography>
              <RadioGroup value={system} onChange={e => setSystem(e.target.value)}>
                {SYSTEMS.map(s => (
                  <FormControlLabel key={s.value} value={s.value} control={<Radio size="small" />} label={s.label} />
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 0.5 }}>Zodiac</Typography>
              <RadioGroup value={zodiac} onChange={e => setZodiac(e.target.value)}>
                {ZODIACS.map(z => (
                  <FormControlLabel key={z.value} value={z.value} control={<Radio size="small" />} label={z.label} />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
            Configuration: <strong>{describeConfiguration(system, zodiac)}</strong>
            {system === 'dual' && ' — each body contributes its geocentric AND heliocentric longitude'}
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 0.5 }}>
            Bodies to average ({usable.length} selected)
          </Typography>
          <FormGroup row>
            {BODIES.map(body => {
              const blocked = unavailable.includes(body);
              return (
                <FormControlLabel
                  key={body}
                  sx={{ minWidth: 118 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(body)}
                      disabled={blocked}
                      onChange={() => toggleBody(body)}
                    />
                  }
                  label={<span style={{ opacity: blocked ? 0.45 : 1 }}>{body}</span>}
                />
              );
            })}
          </FormGroup>

          {droppedBySystem.length > 0 && (
            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
              {droppedBySystem.join(', ')} {droppedBySystem.length === 1 ? 'has' : 'have'} no
              heliocentric position, so {droppedBySystem.length === 1 ? 'it is' : 'they are'} not
              included in a Heliocentric study. Choose Geo + Helio to include
              {droppedBySystem.length === 1 ? ' it' : ' them'} geocentrically.
            </Alert>
          )}
          {partial.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
              {partial.join(', ')} will contribute a geocentric longitude only, where every other
              body contributes two. That gives {partial.length === 1 ? 'it' : 'them'} half the
              weight in the average.
            </Alert>
          )}
          {usable.length === 0 && (
            <Alert severity="error" sx={{ mt: 1, py: 0 }}>Select at least one body.</Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1 }}>Line</Typography>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              label="Price per 360°" size="small" type="number" sx={{ width: 140 }}
              value={pricePer360} onChange={e => setPricePer360(e.target.value)}
            />
            <FormControl size="small" sx={{ width: 140 }}>
              <Typography variant="caption">Colour</Typography>
              <Select value={color} onChange={e => setColor(e.target.value)}>
                {COLORS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 130 }}>
              <Typography variant="caption">Line style</Typography>
              <Select value={lineType} onChange={e => setLineType(e.target.value)}>
                {LINE_TYPES.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 130 }}>
              <Typography variant="caption">Thickness</Typography>
              <Select value={thickness} onChange={e => setThickness(e.target.value)}>
                {['thin', 'medium', 'thick'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" gap={2} alignItems="center" sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={autoFitOctaves}
                                 onChange={e => setAutoFitOctaves(e.target.checked)} />}
              label="Auto-fit octaves"
            />
            <TextField label="From" size="small" type="number" sx={{ width: 90 }}
                       disabled={autoFitOctaves} value={octaveStart}
                       onChange={e => setOctaveStart(e.target.value)} />
            <TextField label="To" size="small" type="number" sx={{ width: 90 }}
                       disabled={autoFitOctaves} value={octaveEnd}
                       onChange={e => setOctaveEnd(e.target.value)} />
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="outlined" color="secondary">Close</Button>
        <Button onClick={handleRun} variant="contained" disabled={usable.length === 0}>Run</Button>
      </DialogActions>
    </Dialog>
  );
};

AverageLongitudeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
};

export default AverageLongitudeDialog;
